// ── Daily Challenge System ──
// Deterministic daily puzzle selection using date-based seeding.
// Streak tracking with grace period logic.

import { listPuzzles } from '@/engine/loader';
import type { PlayerProgress } from '@/engine/progression';

// ── Day-of-week difficulty mapping ──
// 0=Sunday, 1=Monday, ..., 6=Saturday

interface DifficultyRange {
  minTier: number;
  maxTier: number;
}

const DAY_DIFFICULTY: Record<number, DifficultyRange> = {
  0: { minTier: 3, maxTier: 5 },  // Sunday
  1: { minTier: 2, maxTier: 3 },  // Monday
  2: { minTier: 3, maxTier: 4 },  // Tuesday
  3: { minTier: 4, maxTier: 5 },  // Wednesday
  4: { minTier: 5, maxTier: 6 },  // Thursday
  5: { minTier: 6, maxTier: 7 },  // Friday
  6: { minTier: 7, maxTier: 9 },  // Saturday
};

// ── Public API ──

export function formatDateKey(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getDailyDifficulty(date: Date): DifficultyRange {
  const dayOfWeek = date.getUTCDay();
  return DAY_DIFFICULTY[dayOfWeek] ?? { minTier: 3, maxTier: 5 };
}

export function getDailyPuzzleId(date: Date): string {
  const { minTier, maxTier } = getDailyDifficulty(date);

  // Get all registered puzzles within the tier range
  const allPuzzles = listPuzzles();
  const eligible = allPuzzles.filter(
    (p) => p.tier >= minTier && p.tier <= maxTier,
  );

  // Fallback: if no puzzles in range, use all puzzles
  const pool = eligible.length > 0 ? eligible : allPuzzles;
  if (pool.length === 0) {
    return ''; // No puzzles registered at all
  }

  // Deterministic seed from date
  const seed =
    date.getUTCFullYear() * 10000 +
    (date.getUTCMonth() + 1) * 100 +
    date.getUTCDate();

  // Simple deterministic hash to pick a puzzle
  const index = seededIndex(seed, pool.length);

  const selected = pool[index];
  if (!selected) return pool[0]?.id ?? 'ch1-01';
  return selected.id;
}

export function isStreakAlive(lastSolveDate: string | null, now: Date): boolean {
  if (lastSolveDate === null) return false;

  const todayKey = formatDateKey(now);

  // If solved today, streak is alive
  if (lastSolveDate === todayKey) return true;

  // Check if solved yesterday
  const yesterday = new Date(now);
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  const yesterdayKey = formatDateKey(yesterday);

  if (lastSolveDate === yesterdayKey) return true;

  // Grace period: if now is before noon UTC, day-before-yesterday counts
  const hoursUTC = now.getUTCHours();
  if (hoursUTC < 12) {
    const dayBeforeYesterday = new Date(now);
    dayBeforeYesterday.setUTCDate(dayBeforeYesterday.getUTCDate() - 2);
    const dbKey = formatDateKey(dayBeforeYesterday);
    if (lastSolveDate === dbKey) return true;
  }

  return false;
}

export function updateStreak(
  progress: PlayerProgress,
  solveDate: string,
): PlayerProgress {
  const { lastSolveDate, currentStreak, longestStreak } = progress.daily;

  // If already solved today, no change
  if (lastSolveDate === solveDate) {
    return progress;
  }

  // Check if lastSolveDate is yesterday
  const solveDay = parseDate(solveDate);
  let newStreak: number;

  if (lastSolveDate !== null) {
    const lastDay = parseDate(lastSolveDate);
    const diffMs = solveDay.getTime() - lastDay.getTime();
    const diffDays = Math.round(diffMs / (24 * 60 * 60 * 1000));

    if (diffDays === 1) {
      // Consecutive day — increment streak
      newStreak = currentStreak + 1;
    } else {
      // Gap — reset streak to 1
      newStreak = 1;
    }
  } else {
    // First ever solve
    newStreak = 1;
  }

  const newLongest = Math.max(longestStreak, newStreak);

  return {
    journey: { ...progress.journey },
    daily: {
      ...progress.daily,
      currentStreak: newStreak,
      longestStreak: newLongest,
      lastSolveDate: solveDate,
    },
    stats: { ...progress.stats },
  };
}

// ── Internal Helpers ──

function seededIndex(seed: number, length: number): number {
  // Simple deterministic hash (mulberry32-inspired)
  let t = (seed + 0x6d2b79f5) | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const result = ((t ^ (t >>> 14)) >>> 0);
  return result % length;
}

function parseDate(dateStr: string): Date {
  const parts = dateStr.split('-').map(Number);
  return new Date(Date.UTC(parts[0] ?? 2026, (parts[1] ?? 1) - 1, parts[2] ?? 1));
}
